<template>
  <FormSheet
    v-model:open="drawerVisible"
    :title="$t('Поиск')"
    :width="548"
    :show-footer="false"
    @close="closeDrawer"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col gap-6">
        <div class="flex items-center justify-between">
          <div class="flex gap-2">
            <p class="where">{{ $t("Где искать") }}</p>
            <DropdownWrapper
              v-model="selectedRegistry"
              :options="registryOptions"
            />
          </div>
          <Checkbox v-model="searchByRK" class="text">
            {{ $t("Искать по РК") }}
          </Checkbox>
        </div>

        <div v-if="selectedRegistry === registryOptions[0]" class="input">
          <div class="relative">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            />
            <Input
              v-model="searchQuery"
              :placeholder="$t('Ключевые слова / ИИН / БИН / № действия и др.')"
              class="pl-9"
            />
          </div>

          <div class="flex gap-2">
            <Input
              v-model="startDate"
              type="date"
              :placeholder="$t('Дата С')"
              class="w-full"
            />
            <Input
              v-model="endDate"
              type="date"
              :placeholder="$t('Дата По')"
              class="w-full"
            />
          </div>
        </div>

        <div v-else-if="selectedRegistry === registryOptions[1]" class="input">
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1.5">
              <Label>{{ $t("Номер нотариального действия") }}</Label>
              <Input
                v-model="formItem.actNumber"
                :placeholder="$t('Введите')"
                class="w-full"
                :class="{ 'border-destructive': formErrors.actNumber }"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <Label>{{ $t("Дата совершения действия") }}</Label>
              <Input
                v-model="formItem.actDate"
                type="date"
                placeholder="Дата совершения действия"
                class="w-full"
                :class="{ 'border-destructive': formErrors.actDate }"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <Label>{{ $t("ФИО нотариуса") }}</Label>
              <Input
                v-model="formItem.notaryName"
                placeholder="Введите имя нотариуса"
                class="w-full"
                :class="{ 'border-destructive': formErrors.notaryName }"
              />
            </div>
          </div>
        </div>

        <div v-else-if="selectedRegistry === registryOptions[2]" class="input">
          <div class="flex gap-2">
            <Input
              v-model="startDate"
              type="date"
              :placeholder="$t('Дата С')"
              class="w-full"
            />
            <Input
              v-model="endDate"
              type="date"
              :placeholder="$t('Дата По')"
              class="w-full"
            />
          </div>
          <div class="relative">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            />
            <Input
              v-model="additionalField1"
              :placeholder="$t('Дополнительное поле')"
              class="pl-9"
            />
          </div>
        </div>
      </div>
      <div class="flex justify-between items-center">
        <p class="text-sm font-medium text-muted-foreground">
          {{ $t("Найдено") }}
        </p>
        <BaseButton @click="performSearch">{{ $t("Найти") }}</BaseButton>
      </div>
    </div>
  </FormSheet>
</template>

<script>
import BaseButton from "@/shared/ui/custom/components/BaseButton.vue";
import DropdownWrapper from "@/shared/ui/custom/components/DropdownWrapper.vue";
import Checkbox from "@/shared/ui/checkbox/Checkbox.vue";
import { FormSheet } from "@/shared/ui/form-sheet";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Search } from "lucide-vue-next";

export default {
  components: {
    BaseButton,
    DropdownWrapper,
    Checkbox,
    FormSheet,
    Input,
    Label,
    Search,
  },

  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      drawerVisible: this.modelValue,

      searchQuery: "",
      searchResults: [],
      selectedRegistry: "",
      searchByRK: false,
      startDate: "",
      endDate: "",
      isStartDateFocused: false,

      formItem: {
        actNumber: "",
        actDate: "",
        notaryName: "",
      },

      formErrors: {},

      additionalField1: "",

      registryOptions: [
        this.$t("Реестр действий"),
        this.$t("Исполнительные надписи"),
        this.$t("Мои запросы"),
      ],
    };
  },

  watch: {
    modelValue: {
      handler(newValue) {
        this.drawerVisible = newValue;
        if (newValue) {
          this.searchQuery = "";
          this.selectedRegistry = this.registryOptions[0];
          this.searchByRK = false;
          this.startDate = "";
          this.endDate = "";
          this.searchResults = [];

          this.formItem = {
            actNumber: "",
            actDate: "",
            notaryName: "",
          };

          this.formErrors = {};
          this.additionalField1 = "";
        }
      },
      immediate: true,
    },

    drawerVisible(newValue) {
      this.$emit("update:modelValue", newValue);
    },
  },

  methods: {
    closeDrawer() {
      this.drawerVisible = false;
    },

    validateForm() {
      this.formErrors = {};
      let valid = true;
      if (!this.formItem.actNumber) {
        this.formErrors.actNumber = true;
        valid = false;
      }
      if (!this.formItem.actDate) {
        this.formErrors.actDate = true;
        valid = false;
      }
      if (!this.formItem.notaryName) {
        this.formErrors.notaryName = true;
        valid = false;
      }
      return valid;
    },

    performSearch() {
      if (this.selectedRegistry === this.registryOptions[1]) {
        if (!this.validateForm()) {
          return;
        }
        this.executeSearch();
      } else {
        this.executeSearch();
      }
    },

    executeSearch() {
      const searchParams = {
        registry: this.selectedRegistry,
        searchByRK: this.searchByRK,
      };

      if (this.selectedRegistry === this.registryOptions[0]) {
        searchParams.query = this.searchQuery;
        searchParams.startDate = this.startDate;
        searchParams.endDate = this.endDate;
      } else if (this.selectedRegistry === this.registryOptions[1]) {
        searchParams.actNumber = this.formItem.actNumber;
        searchParams.actDate = this.formItem.actDate;
        searchParams.notaryName = this.formItem.notaryName;
      } else if (this.selectedRegistry === this.registryOptions[2]) {
        searchParams.startDate = this.startDate;
        searchParams.endDate = this.endDate;
        searchParams.additionalField1 = this.additionalField1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.icon {
  @apply w-8 h-8 flex items-center justify-center cursor-pointer;
  svg {
    color: hsl(var(--foreground));
    &:hover {
      color: hsl(var(--foreground));
    }
  }
}

.where {
  @apply flex items-center text-sm font-medium text-muted-foreground;
}

.text {
  @apply leading-6 font-medium text-foreground rounded-md cursor-pointer;
  font-size: 15px;
}

.input {
  @apply flex flex-col gap-2;
}
</style>
